import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import HomeRounded from "@mui/icons-material/HomeRounded";
import SettingsRounded from "@mui/icons-material/SettingsRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import LogoutRounded from "@mui/icons-material/LogoutRounded";
import FolderRounded from "@mui/icons-material/FolderRounded";
import AppsRounded from "@mui/icons-material/AppsRounded";
import HealthAndSafetyRounded from "@mui/icons-material/HealthAndSafetyRounded";
import NotificationsRounded from "@mui/icons-material/NotificationsRounded";
import QueryStatsRounded from "@mui/icons-material/QueryStatsRounded";
import TimelineRounded from "@mui/icons-material/TimelineRounded";
import AccountTreeRounded from "@mui/icons-material/AccountTreeRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import VpnKeyRounded from "@mui/icons-material/VpnKeyRounded";
import ChecklistRounded from "@mui/icons-material/ChecklistRounded";
import StorageRounded from "@mui/icons-material/StorageRounded";
import { useMemo, type ComponentType } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useHierarchyContext } from "../../contexts/HierarchyContext";

type NavScope = "global" | "tenant" | "product" | "service";

const NAV_ITEMS = [
  { key: "home", label: "Home", scope: "global" as NavScope, icon: HomeRounded },
  { key: "search", label: "Search", scope: "global" as NavScope, icon: SearchRounded },
  { key: "settings", label: "Settings", scope: "global" as NavScope, icon: SettingsRounded },
  { key: "tenantProducts", label: "Products", scope: "tenant" as NavScope, icon: FolderRounded },
  { key: "tenantSettings", label: "Settings", scope: "tenant" as NavScope, icon: SettingsRounded },
  { key: "productServices", label: "Services", scope: "product" as NavScope, icon: AppsRounded },
  { key: "health", label: "Health", scope: "service" as NavScope, icon: HealthAndSafetyRounded },
  { key: "metrics", label: "Metrics", scope: "service" as NavScope, icon: QueryStatsRounded },
  { key: "traces", label: "Traces Query", scope: "service" as NavScope, icon: TimelineRounded },
  { key: "spans", label: "Spans Query", scope: "service" as NavScope, icon: AccountTreeRounded },
  { key: "logs", label: "Logs Query", scope: "service" as NavScope, icon: DescriptionRounded },
  { key: "alerts", label: "Alerts", scope: "service" as NavScope, icon: NotificationsRounded },
  { key: "instances", label: "Instances", scope: "service" as NavScope, icon: StorageRounded },
  { key: "health-checks", label: "Health Checks", scope: "service" as NavScope, icon: ChecklistRounded },
  { key: "ingress-tokens", label: "Ingress Tokens", scope: "service" as NavScope, icon: VpnKeyRounded },
] as const;

export interface SidebarNavProps {
  collapsed?: boolean;
  /** Called when user selects a nav item (e.g. to close mobile drawer) */
  onNavigate?: () => void;
  /** Called when user clicks Search (opens command palette) */
  onOpenCommandPalette?: () => void;
  /** Called when user clicks Log out */
  onLogout?: () => void;
}

export function SidebarNav({
  collapsed = false,
  onNavigate,
  onOpenCommandPalette,
  onLogout,
}: SidebarNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hierarchy, tenants, products, services } = useHierarchyContext();

  const effectiveTenantSlug = hierarchy?.tenant?.slug;
  const effectiveProductSlug = hierarchy?.product?.slug;
  const effectiveServiceSlug = hierarchy?.service?.slug;

  const filteredProducts = useMemo(() => {
    return products.filter((p) => p.tenantSlug === effectiveTenantSlug);
  }, [products, effectiveTenantSlug]);

  const filteredServices = useMemo(() => {
    return services.filter((s) => s.productSlug === effectiveProductSlug);
  }, [services, effectiveProductSlug]);

  const currentKey = (() => {
    if (location.pathname === "/" || location.pathname === "/home") return "home";
    if (location.pathname.startsWith("/settings")) return "settings";
    // search has no route, never selected
    if (location.pathname.startsWith("/tenants/")) {
      const parts = location.pathname.split("/").filter(Boolean);
      if (parts[2] === "settings") return "tenantSettings";
      return "tenantProducts";
    }
    if (location.pathname.startsWith("/products/")) return "productServices";
    if (location.pathname.startsWith("/services/")) {
      const parts = location.pathname.split("/").filter(Boolean);
      const section = parts[2] ?? "health";
      if (section === "traces" && parts[3]) return "traces";
      return section;
    }
    return "home";
  })();

  const handleNavClick = (item: (typeof NAV_ITEMS)[number]) => {
    if (item.key === "home") {
      navigate("/home");
      onNavigate?.();
      return;
    }
    if (item.key === "settings") {
      navigate("/settings");
      onNavigate?.();
      return;
    }
    if (item.key === "search") {
      onOpenCommandPalette?.();
      onNavigate?.();
      return;
    }
    if (item.key === "tenantProducts") {
      if (!effectiveTenantSlug) return;
      navigate(`/tenants/${encodeURIComponent(effectiveTenantSlug)}/products`);
      onNavigate?.();
      return;
    }
    if (item.key === "tenantSettings") {
      if (!effectiveTenantSlug) return;
      navigate(`/tenants/${encodeURIComponent(effectiveTenantSlug)}/settings`);
      onNavigate?.();
      return;
    }
    if (item.key === "productServices") {
      if (!effectiveProductSlug) return;
      navigate(`/products/${encodeURIComponent(effectiveProductSlug)}/services`);
      onNavigate?.();
      return;
    }
    if (!hierarchy?.service?.slug) return;
    navigate(`/services/${encodeURIComponent(hierarchy.service.slug)}/${item.key}`);
    onNavigate?.();
  };

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.scope === "global") return true;
    if (item.scope === "tenant") return !!effectiveTenantSlug;
    if (item.scope === "product") return !!effectiveProductSlug;
    if (item.scope === "service") return !!effectiveServiceSlug;
    return false;
  });

  const globalItems = visibleItems.filter((i) => i.scope === "global");
  const tenantItems = visibleItems.filter((i) => i.scope === "tenant");
  const productItems = visibleItems.filter((i) => i.scope === "product");
  const serviceItems = visibleItems.filter((i) => i.scope === "service");

  const showAllTenants = !effectiveTenantSlug && tenants.length > 0;
  const showAllProducts = !effectiveProductSlug && filteredProducts.length > 0 && !showAllTenants;
  const showAllServices = !effectiveServiceSlug && filteredServices.length > 0 && !showAllProducts && !showAllTenants;

  const renderEntityItem = (
    label: string,
    path: string,
    isActive: boolean,
    Icon: ComponentType<{ fontSize?: "small" | "medium" | "large" }>,
  ) => (
    <ListItem key={path} disablePadding sx={{ display: "block" }}>
      <ListItemButton
        onClick={() => {
          navigate(path);
          onNavigate?.();
        }}
        selected={isActive}
        sx={{
          borderRadius: 1,
          mx: 1,
          mb: 0.5,
          minHeight: 40,
          justifyContent: collapsed ? "center" : "flex-start",
          "&.Mui-selected": {
            bgcolor: "rgba(79, 70, 229, 0.15)",
            "&:hover": {
              bgcolor: "rgba(79, 70, 229, 0.2)",
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 36,
            mr: collapsed ? 0 : 1.5,
            color: isActive ? "primary.main" : "text.secondary",
          }}
        >
          <Icon fontSize="small" />
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              variant: "body2",
              fontWeight: isActive ? 600 : 400,
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );

  const renderGroup = (items: typeof visibleItems) =>
    items.map((item) => {
      const isActive = currentKey === item.key;
      const Icon = item.icon;
      return (
        <ListItem key={item.key} disablePadding sx={{ display: "block" }}>
          <ListItemButton
            onClick={() => handleNavClick(item)}
            selected={isActive}
            title={item.key === "search" ? "Search (Ctrl+K)" : undefined}
            aria-label={item.key === "search" ? "Open command palette (Ctrl+K)" : undefined}
            sx={{
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              minHeight: 40,
              justifyContent: collapsed ? "center" : "flex-start",
              "&.Mui-selected": {
                bgcolor: "rgba(79, 70, 229, 0.15)",
                "&:hover": {
                  bgcolor: "rgba(79, 70, 229, 0.2)",
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: collapsed ? 0 : 36,
                mr: collapsed ? 0 : 1.5,
                color: isActive ? "primary.main" : "text.secondary",
              }}
            >
              <Icon fontSize="small" />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: isActive ? 600 : 400,
                }}
              />
            )}
          </ListItemButton>
        </ListItem>
      );
    });

  const logoutButton = (
    <ListItem disablePadding sx={{ display: "block" }}>
      <ListItemButton
        onClick={() => {
          onLogout?.();
          onNavigate?.();
        }}
        aria-label="Log out"
        sx={{
          borderRadius: 1,
          mx: 1,
          mb: 0.5,
          minHeight: 40,
          justifyContent: collapsed ? "center" : "flex-start",
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 36,
            mr: collapsed ? 0 : 1.5,
            color: "text.secondary",
          }}
        >
          <LogoutRounded fontSize="small" />
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary="Log out"
            primaryTypographyProps={{ variant: "body2", fontWeight: 400 }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
    <Box sx={{ overflow: "auto", py: 2, flex: 1 }}>
      {globalItems.length > 0 && (
        <>
          {!collapsed && (
            <Typography
              variant="caption"
              sx={{ px: 2, py: 0.5, color: "text.secondary", fontWeight: 600, display: "block" }}
            >
              Global
            </Typography>
          )}
          <List dense disablePadding>
            {renderGroup(globalItems)}
          </List>
        </>
      )}
      {(tenantItems.length > 0 || showAllTenants) && (
        <>
          {!collapsed && (
            <>
              <Divider sx={{ my: 1, mx: 2 }} />
              <Typography
                variant="caption"
                sx={{ px: 2, py: 0.5, color: "text.secondary", fontWeight: 600, display: "block" }}
              >
                Tenant
              </Typography>
            </>
          )}
          <List dense disablePadding>
            {showAllTenants
              ? tenants.map((t) =>
                  renderEntityItem(
                    t.name || t.slug,
                    `/tenants/${encodeURIComponent(t.slug)}/products`,
                    location.pathname.startsWith(`/tenants/${t.slug}`),
                    FolderRounded,
                  ),
                )
              : renderGroup(tenantItems)}
          </List>
        </>
      )}
      {(filteredProducts.length > 0 || showAllProducts) && (
        <>
          {!collapsed && (
            <>
              <Divider sx={{ my: 1, mx: 2 }} />
              <Typography
                variant="caption"
                sx={{ px: 2, py: 0.5, color: "text.secondary", fontWeight: 600, display: "block" }}
              >
                Product
              </Typography>
            </>
          )}
          <List dense disablePadding>
            {showAllProducts
              ? filteredProducts.map((p) =>
                  renderEntityItem(
                    p.name || p.slug,
                    `/products/${encodeURIComponent(p.slug)}/services`,
                    location.pathname.startsWith(`/products/${p.slug}`),
                    AppsRounded,
                  ),
                )
              : renderGroup(productItems)}
          </List>
        </>
      )}
      {(filteredServices.length > 0 || showAllServices) && (
        <>
          {!collapsed && (
            <>
              <Divider sx={{ my: 1, mx: 2 }} />
              <Typography
                variant="caption"
                sx={{ px: 2, py: 0.5, color: "text.secondary", fontWeight: 600, display: "block" }}
              >
                Service
              </Typography>
            </>
          )}
          <List dense disablePadding>
            {showAllServices
              ? filteredServices.map((s) =>
                  renderEntityItem(
                    s.name || s.slug,
                    `/services/${encodeURIComponent(s.slug)}/health`,
                    location.pathname.startsWith(`/services/${s.slug}`),
                    HealthAndSafetyRounded,
                  ),
                )
              : renderGroup(serviceItems)}
          </List>
        </>
      )}
    </Box>
    {onLogout && (
      <>
        <Box sx={{ flexShrink: 0 }} />
        <Box sx={{ borderTop: "1px solid", borderColor: "divider", py: 1 }}>
          <List dense disablePadding>
            {logoutButton}
          </List>
        </Box>
      </>
    )}
    </Box>
  );
}
